import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Upload, FileVideo, CheckCircle, Volume2, VolumeX } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import { analyticsApi } from '../services/api';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [stats, setStats] = useState({ total_videos: 0, total_clips: 0, total_downloads: 0, total_users: 0 });
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Only redirect if this is a user interaction, not a bot/crawler
    const isBot = /bot|crawler|spider|crawling/i.test(navigator.userAgent);
    
    // Check if auto-redirect is disabled (for local development/testing)
    // Can be disabled via:
    // 1. Environment variable: REACT_APP_DISABLE_AUTO_LOGIN_REDIRECT=true
    // 2. localStorage: localStorage.setItem('disable_auto_redirect', 'true')
    // 3. Console: window.disableAutoRedirect() or window.enableAutoRedirect()
    const disableAutoRedirect = process.env.REACT_APP_DISABLE_AUTO_LOGIN_REDIRECT === 'true' || 
                                localStorage.getItem('disable_auto_redirect') === 'true';
    
    // Auto-redirect logged-in users to dashboard (unless disabled)
    if (currentUser && !isBot && !disableAutoRedirect) {
      navigate('/dashboard');
      return;
    }
    
    setIsVisible(true);
    
    // Auto-cycle through demo steps
    const interval = setInterval(() => {
      setDemoStep(prev => (prev + 1) % 4);
    }, 3000);

    // Load real stats from backend
    const loadStats = async () => {
      try {
        const realStats = await analyticsApi.getPublicStats();
        setStats(realStats);
        setStatsLoaded(true);
      } catch (error) {
        console.error('Failed to load stats:', error);
        // Use fallback stats
        setStats({ 
          total_videos: 1247, 
          total_clips: 8934, 
          total_downloads: 2156,
          total_users: 892 
        });
        setStatsLoaded(true);
      }
    };

    loadStats();
    
    return () => {
      clearInterval(interval);
    };
  }, [currentUser, navigate]);


  const handleGetStarted = () => {
    navigate('/upload');
  };

  const _scrollToDemo = () => {
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      const newMutedState = !isVideoMuted;
      videoRef.current.muted = newMutedState;
      setIsVideoMuted(newMutedState);
    }
  };

  const _demoSteps = [
    {
      title: "Upload your video",
      description: "Drop any video file",
      visual: "📹",
      color: "from-gray-600 to-gray-700",
      active: demoStep === 0
    },
    {
      title: "AI finds highlights",
      description: "Analyzing moments...",
      visual: "🤖",
      color: "from-gray-700 to-gray-800",
      active: demoStep === 1
    },
    {
      title: "Clips generated",
      description: "3 clips ready",
      visual: "✨",
      color: "from-gray-800 to-black",
      active: demoStep === 2
    },
    {
      title: "Export & share",
      description: "Download or share",
      visual: "📤",
      color: "from-red-600 to-red-700",
      active: demoStep === 3
    }
  ];

  const landingSchemaData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "ClipThis - #1 AI Video Clipping Tool 2025 | Free YouTube Downloader",
      "description": "🚀 #1 AI video clipping tool 2025! Free YouTube downloader (MP4/MP3) + AI-powered video clipping. Create viral clips automatically from streams, gaming videos & long content. Used by 100k+ creators worldwide.",
      "url": "https://clipthis.io",
      "inLanguage": "en-US",
      "isPartOf": {
        "@type": "WebSite",
        "name": "ClipThis",
        "url": "https://clipthis.io"
      },
      "about": {
        "@type": "Thing",
        "name": "AI Video Clipping and YouTube Downloading"
      },
      "mainEntity": {
        "@type": "SoftwareApplication",
        "name": "ClipThis",
        "alternateName": "ClipThis AI Video Editor",
        "applicationCategory": ["VideoEditingApplication", "MultimediaApplication"],
        "operatingSystem": ["Web Browser", "Windows", "macOS", "Linux", "iOS", "Android"],
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": statsLoaded ? stats.total_users.toString() : "2847",
          "bestRating": "5",
          "worstRating": "1"
        },
        "featureList": [
          "Free YouTube video downloading (MP4/MP3)",
          "AI-powered highlight detection",
          "Automatic viral clip generation",
          "Multiple video format support",
          "TikTok, YouTube Shorts, Instagram Reels optimization",
          "Gaming stream clip extraction",
          "Reaction video highlights",
          "No registration required",
          "Real-time processing",
          "Social media ready exports"
        ],
        "screenshot": "https://clipthis.io/screenshots/main-interface.jpg",
        "video": {
          "@type": "VideoObject",
          "name": "ClipThis Demo - AI Video Clipping in Action",
          "description": "See how ClipThis automatically creates viral clips from long videos",
          "thumbnailUrl": "https://clipthis.io/demo-thumbnail.jpg",
          "uploadDate": "2025-01-01T00:00:00+00:00",
          "contentUrl": "https://clipthis.io/demo-video.mp4"
        }
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ClipThis",
      "url": "https://clipthis.io",
      "logo": {
        "@type": "ImageObject",
        "url": "https://clipthis.io/logo.png"
      },
      "sameAs": [
        "https://twitter.com/ClipThisAI",
        "https://youtube.com/@ClipThis",
        "https://tiktok.com/@clipthis",
        "https://instagram.com/clipthis"
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is ClipThis free to use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! ClipThis is completely free to use. You can download YouTube videos and create AI-powered clips without any cost or registration required."
          }
        },
        {
          "@type": "Question",
          "name": "How does the AI video clipping work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our AI analyzes your videos for audio spikes, facial expressions, and exciting moments. It automatically creates clips from these highlights, perfect for TikTok, YouTube Shorts, and Instagram Reels."
          }
        },
        {
          "@type": "Question",
          "name": "Can I download YouTube videos for free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Our free YouTube downloader lets you download videos as MP4 or extract audio as MP3. No registration required and completely free to use."
          }
        },
        {
          "@type": "Question",
          "name": "What video formats are supported?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "ClipThis supports MP4, MOV, AVI, and most common video formats. We can export clips optimized for TikTok, YouTube Shorts, Instagram Reels, and other social platforms."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Create Viral Clips with AI",
      "description": "Learn how to transform long videos into viral clips using AI-powered video analysis",
      "image": "https://clipthis.io/images/how-to-create-viral-clips.jpg",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Upload Your Video",
          "text": "Drop any video file into ClipThis. We support MP4, MOV, AVI, and most major video formats.",
          "image": "https://clipthis.io/images/step-1-upload.jpg"
        },
        {
          "@type": "HowToStep",
          "name": "AI Finds Best Moments",
          "text": "Our AI analyzes your video for audio spikes, facial expressions, and engaging moments automatically.",
          "image": "https://clipthis.io/images/step-2-analysis.jpg"
        },
        {
          "@type": "HowToStep",
          "name": "Download Viral Clips",
          "text": "Get clips ready for TikTok, YouTube Shorts, Instagram Reels, and other social media platforms.",
          "image": "https://clipthis.io/images/step-3-download.jpg"
        }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="ClipThis - #1 AI Video Clipping Tool 2025 | Free YouTube Downloader"
        description="🚀 #1 AI video clipping tool 2025! Free YouTube downloader (MP4/MP3) + AI-powered video clipping. Create viral clips automatically from streams, gaming videos & long content. Used by 100k+ creators worldwide."
        keywords="AI video clipping 2025, automatic video editing, viral clips generator, free youtube downloader, youtube to mp4, youtube to mp3, content creation tools, streaming clips, gaming highlights, video AI editor, content creator tools, viral video maker, auto clip generator, twitch highlights, youtube shorts creator, tiktok clips maker, instagram reels editor, AI highlight detection, smart video editing, automated content creation, stream to shorts, gaming clip maker, reaction clip generator, best AI video editor 2025, free video clipping tool, AI powered video editor, viral content creator, social media video tool"
        url="https://clipthis.io"
        schemaData={landingSchemaData}
      />
      <div className="min-h-screen bg-white">
        {/* Clean Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center px-6">
          {/* Subtle dot pattern */}
          <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
          
          <div className={`relative z-10 max-w-7xl mx-auto text-center transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            
            {/* Clean headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold tracking-tighter text-black mb-6 leading-tight">
            #1 AI Video Clipping Tool 2025<br/>
            <span className="text-red-600">FREE</span> YouTube Downloader & <span className="text-red-600">VIRAL CLIPS</span>
            </h1>
            
            {/* <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-8">
              Free YouTube downloader + AI-powered video clipping in one platform
            </p> */}

            {/* Quick feature highlights */}
            {/* <div className="flex flex-wrap justify-center gap-6 mb-12 text-sm">
              <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-200 shadow-sm">
                <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Free YouTube Downloads
              </div>
              <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-200 shadow-sm">
                <Sparkles className="w-4 h-4 text-red-500 mr-2" />
                AI Video Clipping
              </div>
              <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-200 shadow-sm">
                <Download className="w-4 h-4 text-red-500 mr-2" />
                MP4 & MP3 Export
              </div>
            </div> */}

            {/* Simple stats */}
            {statsLoaded && (stats.total_videos > 0 || stats.total_clips > 0 || stats.total_downloads >= 0 || stats.total_users > 0) && (
              <div className="flex justify-center items-center space-x-6 mb-12 text-sm text-gray-500">
                {stats.total_videos > 0 && (
                  <>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-black">
                        {stats.total_videos.toLocaleString()}
                      </div>
                      <div>Videos processed</div>
                    </div>
                    <div className="w-px h-6 bg-gray-200"></div>
                  </>
                )}
                {stats.total_clips > 0 && (
                  <>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-black">
                        {stats.total_clips.toLocaleString()}
                      </div>
                      <div>Clips generated</div>
                    </div>
                    <div className="w-px h-6 bg-gray-200"></div>
                  </>
                )}
                <div className="text-center">
                  <div className="text-lg font-semibold text-black">
                    {(stats.total_downloads || 0).toLocaleString()}
                  </div>
                  <div>Downloads</div>
                </div>
                {stats.total_users > 0 && (
                  <>
                    <div className="w-px h-6 bg-gray-200"></div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-black">
                        {stats.total_users.toLocaleString()}
                      </div>
                      <div>Creators</div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Demo Video */}
            <div className="mb-12">
              <div className="bg-white rounded-2xl p-6 max-w-5xl mx-auto border border-gray-100 shadow-lg">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  {/* Video player */}
                  <div className="flex-1 max-w-sm mx-auto lg:mx-0">
                    <div className="relative bg-black rounded-xl overflow-hidden shadow-lg cursor-pointer">
                      <video
                        className="w-full h-auto max-h-[600px] object-cover"
                        muted
                        autoPlay
                        loop
                        playsInline
                        disablePictureInPicture
                        poster="/api/placeholder/300/600" // Optional: add a poster image
                        onClick={handleVideoClick}
                        ref={videoRef}
                      >
                        <source src="/demo-showcase.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      
                      {/* Video overlay with title and mute indicator */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3">
                          <div className="text-white text-sm font-medium">
                            ClipThis Demo - Turn 1 hour → 3 viral clips
                          </div>
                          <div className="text-white/80 text-xs mt-1 flex items-center">
                            {isVideoMuted ? (
                              <>
                                <VolumeX className="h-3 w-3 mr-1" />
                                Click to unmute and hear the full process
                              </>
                            ) : (
                              <>
                                <Volume2 className="h-3 w-3 mr-1" />
                                Click to mute
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Accompanying text */}
                  <div className="flex-1 text-left lg:pl-8">
                    <h3 className="text-2xl font-bold text-black mb-4">
                      See ClipThis in action
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Watch how easy it is to transform a long video into multiple viral-ready clips. 
                      Our AI automatically finds the best moments and creates perfectly sized clips for 
                      TikTok, Instagram, and YouTube Shorts.
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                        Automatic highlight detection
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                        Perfect vertical format for social media
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                        Ready to download and share instantly
                      </div>
                    </div>
                    
                    <button
                      onClick={handleGetStarted}
                      className="mt-6 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200 inline-flex items-center"
                    >
                      Try it yourself
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Dual CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/downloads"
                className="px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Download YouTube Videos
              </Link>
              
              <button
                onClick={handleGetStarted}
                className="px-8 py-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-200 flex items-center"
              >
                <Upload className="h-5 w-5 mr-2" />
                Create AI Clips
              </button>
            </div>

            {/* Simple trust indicators */}
            <div className="mt-8 text-sm text-gray-500 text-center">
              Free to try • No credit card required
            </div>
          </div>
        </section>

        {/* YouTube Downloader Highlight Section */}
        <section className="py-12 bg-red-50 relative">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-8">
              <div className="inline-flex items-center bg-red-100 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium mb-3">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Most Popular Feature
              </div>
              
              <h2 className="text-2xl md:text-4xl font-bold text-black mb-3">
                Free YouTube Downloader
              </h2>
              <p className="text-gray-700 max-w-2xl mx-auto">
                Download any YouTube video as MP4 or extract audio as MP3. No registration required, completely free, and works instantly.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100 max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {/* Quick features */}
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="bg-red-600 w-6 h-6 rounded flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm">Multiple quality options (720p, 1080p, 4K)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-red-600 w-6 h-6 rounded flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm">High-quality MP3 audio extraction</span>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-red-600 w-6 h-6 rounded flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm">Lightning fast downloads</span>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-red-600 w-6 h-6 rounded flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm">100% safe & private - no registration</span>
                  </div>
                  
                  <div className="pt-2">
                    <Link
                      to="/downloads"
                      className="inline-flex items-center px-5 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      Try Free Now
                    </Link>
                  </div>
                </div>

                {/* Simplified demo */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-center mb-4">
                    <p className="text-gray-600 text-sm font-medium">Just paste & download</p>
                  </div>
                  
                  {/* Mock URL input */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3 mb-3">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      <span className="text-gray-500 text-xs">youtube.com/watch?v=example</span>
                    </div>
                  </div>
                  
                  {/* Download options */}
                  <div className="space-y-2">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-center justify-between">
                      <div className="flex items-center">
                        <FileVideo className="w-4 h-4 text-red-600 mr-2" />
                        <span className="text-xs font-medium text-black">MP4 • 1080p</span>
                      </div>
                      <div className="bg-red-600 text-white px-2 py-1 rounded text-xs">Download</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13" />
                        </svg>
                        <span className="text-xs font-medium text-black">MP3 • 320kbps</span>
                      </div>
                      <div className="bg-gray-600 text-white px-2 py-1 rounded text-xs">Download</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white relative">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                All Your Video Tools in One Place
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                From AI-powered clip generation to YouTube downloading - everything you need for content creation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* AI Video Clipper */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="bg-black w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">AI Video Clipper</h3>
                <p className="text-gray-600 mb-4">
                  Upload long videos and get viral clips automatically. Our AI finds the best moments and creates perfect clips for social media.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    Smart AI analysis
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    Vertical format ready
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    Multiple export formats
                  </div>
                </div>
              </div>

              {/* YouTube Downloader */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="bg-red-600 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">YouTube Downloader</h3>
                <p className="text-gray-600 mb-4">
                  Download YouTube videos as MP4 or extract audio as MP3. Free, fast, and no registration required.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    Multiple video qualities
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    MP3 audio extraction
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    No registration needed
                  </div>
                </div>
                <div className="mt-6">
                  <Link
                    to="/downloads"
                    className="inline-flex items-center text-red-600 hover:text-red-700 font-medium text-sm"
                  >
                    Try it free
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* Format Converter */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="bg-gray-800 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">Smart Editing</h3>
                <p className="text-gray-600 mb-4">
                  Advanced editing features including vertical format conversion, blur backgrounds, and facecam separation.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    Auto vertical conversion
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    Blur backgrounds
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                    Facecam separation
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            {/* <div className="text-center mt-12">
              <div className="bg-white rounded-2xl p-8 max-w-4xl mx-auto border border-gray-200 shadow-sm">
                <h3 className="text-2xl font-bold text-black mb-4">
                  Start Creating Better Content Today
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Join thousands of creators using ClipThis to turn their long videos into viral clips and grow their audience.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/upload"
                    className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200 inline-flex items-center justify-center"
                  >
                    Start Clipping Free
                  </Link>
                  <Link
                    to="/downloads"
                    className="px-6 py-3 border border-gray-300 text-black rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors duration-200 font-medium inline-flex items-center justify-center"
                  >
                    Try YouTube Downloader
                  </Link>
                </div>
              </div>
            </div> */}
          </div>
        </section>

        {/* How It Works Section */}
        <section id="demo" className="py-20 bg-gray-50 relative">
          {/* Subtle dot pattern */}
          <div className="absolute inset-0 dot-pattern-animated opacity-20 pointer-events-none" />
          
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                How it works
              </h2>
              <p className="text-lg text-gray-600">
                From long video to viral clip in 3 steps
              </p>
            </div>

            {/* Mac Window Demo */}
            <div className="mb-16">
              <div className="bg-white rounded-2xl p-6 max-w-4xl mx-auto border border-gray-100 shadow-lg">
                {/* Mock video player with mac window styling */}
                <div className="bg-black rounded-xl p-4 mb-6 relative overflow-hidden">
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-red-500 rounded-md"></div>
                        <div className="w-3 h-3 bg-gray-400 rounded-md"></div>
                        <div className="w-3 h-3 bg-gray-600 rounded-md"></div>
                      </div>
                      <div className="text-white/60 text-sm">ClipThis Processing</div>
                    </div>
                    
                    {/* Simulated video timeline */}
                    <div className="bg-gray-900 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white/80 text-sm">gaming_stream_2024.mp4</span>
                        <span className="text-gray-300 text-sm">● Processing</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-lg h-2 mb-2">
                        <div className="bg-red-600 h-2 rounded-l-lg animate-pulse" style={{ width: '75%' }}></div>
                      </div>
                      <div className="text-white/60 text-xs">Finding highlights... 3 clips detected</div>
                    </div>
                  </div>
                </div>
                
                {/* Simple progress bar */}
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-lg h-2">
                    <div 
                      className={`bg-black h-2 transition-all duration-500 ${((demoStep + 1) / 4) * 100 >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                      style={{ width: `${((demoStep + 1) / 4) * 100}%` }}
                    ></div>
                  </div>
                  <div className="absolute -top-1 transition-all duration-500" style={{ left: `${((demoStep + 1) / 4) * 100}%` }}>
                    <div className="w-4 h-4 bg-white border-2 border-black rounded-full transform -translate-x-1/2"></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              {/* Step 1 */}
              <div className="relative group">
                <div className="relative bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                  <div className="absolute -top-3 left-6">
                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center text-white font-bold text-sm">
                      1
                    </div>
                  </div>
                  
                  {/* Visual mockup */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 mt-2">
                    <div className="bg-white rounded-lg p-3 border border-dashed border-gray-300">
                      <FileVideo className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                      <div className="text-center text-xs text-gray-600">Drop video here</div>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-black mb-2">
                    Upload your video
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Drop any video file. We support all major formats like MP4, MOV, AVI, and more.
                  </p>
                  <div className="flex items-center text-xs text-gray-500">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Supports all formats
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group">
                <div className="relative bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                  <div className="absolute -top-3 left-6">
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      2
                    </div>
                  </div>
                  
                  {/* AI Analysis Visual */}
                  <div className="bg-red-50 rounded-lg p-4 mb-4 mt-2">
                    <div className="relative">
                      <div className="bg-black rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-white text-xs">Analysis</div>
                          <div className="flex space-x-1">
                            <div className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse"></div>
                            <div className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="bg-red-500 h-1 rounded-l-lg w-3/4"></div>
                          <div className="bg-gray-500 h-1 rounded-l-lg w-1/2"></div>
                          <div className="bg-red-600 h-1 rounded-l-lg w-2/3"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-black mb-2">
                    We find the best moments
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Our system analyzes your video to find the most engaging parts automatically.
                  </p>
                  <div className="flex items-center text-xs text-red-600">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Automatic highlight detection
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group">
                <div className="relative bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                  <div className="absolute -top-3 left-6">
                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center text-white font-bold text-sm">
                      3
                    </div>
                  </div>
                  
                  {/* Clips Preview */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 mt-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white rounded p-2 border border-gray-200">
                        <div className="w-full h-4 bg-gray-400 rounded mb-1"></div>
                        <div className="text-xs text-gray-600">Clip 1 • 15s</div>
                      </div>
                      <div className="bg-white rounded p-2 border border-gray-200">
                        <div className="w-full h-4 bg-red-400 rounded mb-1"></div>
                        <div className="text-xs text-gray-600">Clip 2 • 22s</div>
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-black mb-2">
                    Download & share
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Get clips ready for TikTok, Instagram, YouTube Shorts, and more platforms.
                  </p>
                  <div className="flex items-center text-xs text-gray-500">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Platform-optimized
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        

        {/* Simple final CTA */}
        <section className="py-16 bg-white relative">
          {/* Subtle dot pattern */}
          <div className="absolute inset-0 dot-pattern-animated opacity-15 pointer-events-none" />
          
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <h2 className="text-2xl font-bold text-black mb-4">
              Ready to get started?
            </h2>
            <p className="text-gray-600 mb-8">
              Download YouTube videos instantly or create viral clips with AI.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/downloads"
                className="px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200 inline-flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Download YouTube Videos
              </Link>
              <button
                onClick={handleGetStarted}
                className="px-8 py-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-200 inline-flex items-center justify-center"
              >
                <Upload className="h-5 w-5 mr-2" />
                Create AI Clips
              </button>
            </div>
          </div>
        </section>

        {/* Developer Tools (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <section className="py-4 bg-gray-100 border-t border-gray-200">
            <div className="max-w-4xl mx-auto px-6">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-2">🛠️ Developer Tools</div>
                <button
                  onClick={() => {
                    const current = localStorage.getItem('disable_auto_redirect') === 'true';
                    localStorage.setItem('disable_auto_redirect', (!current).toString());
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-gray-800 text-white text-xs rounded hover:bg-gray-700 transition-colors"
                >
                  {localStorage.getItem('disable_auto_redirect') === 'true' 
                    ? '✅ Auto-redirect disabled' 
                    : '❌ Auto-redirect enabled'
                  } (Click to toggle)
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Footer Section */}
        <footer className="py-8 bg-white border-t border-gray-200">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              {/* Logo/Brand */}
              <div className="flex items-center space-x-2">
                <span className="text-xl font-bold text-gray-900">ClipThis</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-600 text-sm">AI Video Clipping</span>
              </div>
              
              {/* Links */}
              <div className="flex items-center space-x-6 text-sm">
                <Link 
                  to="/privacy?from=landing" 
                  className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
                <Link 
                  to="/terms?from=landing" 
                  className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} ClipThis. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Landing; 